import React, { useCallback, useEffect, useId, useRef } from 'react';
import {
  CELL_H,
  CELL_LOCAL,
  CELL_W,
  casualVoronoiPhoto,
} from '../header-image/casual-voronoi-cell';
import './voronoi-header.scss';

/**
 * VoronoiHeader — sfondo dell'header come tassellazione di Voronoi animata, con
 * il bordo inferiore che segue i confini delle celle.
 *
 * Tre proprieta' che il componente garantisce per costruzione, non per taratura:
 *
 * 1. LA CELLA DELLA FOTO E' UNA VERA CELLA DEL DIAGRAMMA.
 *    La foto sta in una cella di forma decisa a mano (CELL_LOCAL), non in una
 *    macchia che la tassellazione produce per conto suo. Il modo di ottenerlo
 *    senza disegnare una forma SOPRA il diagramma e' la RIFLESSIONE: per ogni
 *    lato del poligono si mette un seme fisso nel simmetrico dell'ospite
 *    rispetto a quel lato. L'asse fra ospite e riflesso e' per definizione il
 *    lato stesso, quindi la cella dell'ospite - intersezione di quei semipiani -
 *    E' il poligono, non una sua approssimazione (scarto misurato ~1e-12).
 *
 *    Da qui viene il resto gratis: le celle vicine si accostano al bordo della
 *    foto perche' e' un confine del diagramma come ogni altro, e non resta il
 *    vuoto che una forma disegnata sopra lasciava fra se' e la cella vera del
 *    seme.
 *
 *    La foto e' disegnata in QUESTO svg, ritagliata su quello stesso poligono:
 *    un solo sistema di coordinate per cella e immagine, che quindi non possono
 *    scollarsi. Vedi casual-voronoi.tsx per il perche' l'elemento in pagina sia
 *    rimasto vuoto.
 *
 * 2. NESSUNA CELLA APPARE O SPARISCE.
 *    Chi viene disegnato si decide una sola volta, sulla posizione a riposo del
 *    seme. I semi sotto la linea di taglio non vengono mai disegnati ma restano
 *    nel diagramma e continuano a modellare i vicini: e' la loro deriva a far
 *    respirare il bordo inferiore. Nessun attributo display cambia mai a runtime.
 *
 *    E' qui che il componente si scosta di proposito dal prototipo in
 *    newdevelop/vornoiheader.tsx, da cui viene la deriva: quello, a ogni
 *    fotogramma, nasconde con display:none le celle il cui seme e' passato sotto
 *    la linea e le rimostra quando risale, cosi' il bordo inferiore sfarfalla di
 *    macchie che compaiono e svaniscono.
 *
 *    Che nessuna cella possa sparire per il MOVIMENTO non e' poi una speranza: la
 *    cella di Voronoi di un punto distinto dagli altri non e' mai vuota, quindi
 *    una cella si puo' assottigliare ma non annullare. Verificato anche a
 *    posteriori, simulando la deriva su 200 fotogrammi in 140 secondi: zero celle
 *    degeneri per ogni seme interno al box, in tutte le combinazioni provate.
 *
 * 3. LE ALTRE CELLE SONO PICCOLE, FRASTAGLIATE E ALLA DERIVA.
 *    Griglia fitta, jitter a riposo di +/-0.25 del passo e orbite di Lissajous
 *    fino a 0.4 del passo, con frequenze e fasi indipendenti per asse e per seme:
 *    niente si muove in fase con niente, e non si vede un ritmo comune.
 *
 *    Il compromesso da governare e' fra jitter e orbita, che si contendono lo
 *    stesso spazio fra due semi adiacenti. Misure a passo 60px, sulla cella piu'
 *    piccola del mosaico in rapporto all'area media:
 *
 *      jitter  orbita   spostamento   cella minima
 *      +/-.40    .34       14.8px         14.5%     (prima)
 *      +/-.25    .34       14.8px         29.2%
 *      +/-.25    .40       17.4px         15.2%     (adesso)
 *      +/-.22    .44       19.2px         10.7%
 *      +/-.18    .50       21.8px          7.8%
 *
 *    Da qui la scelta: piu' movimento di prima e, allo stesso tempo, celle
 *    minime non peggiori. Sotto, le schegge iniziano a vedersi.
 *
 *    La VELOCITA' e' invece un parametro gratuito: riparametrizza il tempo, non
 *    cambia gli estremi raggiunti, quindi non tocca la geometria. Se il
 *    movimento va reso piu' evidente, si alza speedRange senza assottigliare
 *    nulla.
 *
 * Il componente e' un livello di sfondo: si posiziona in absolute sul genitore e
 * non ha figli. Il contenuto dell'header gli passa sopra nel flusso normale.
 */

export interface VoronoiHeaderProps {
  /**
   * Elemento che la macchia grande deve contenere (l'immagine del profilo). Se
   * assente, o se misura meno di 8px, la tassellazione resta uniforme: nessuna
   * macchia grande e nessun errore.
   */
  focusRef?: React.RefObject<HTMLElement | null>;
  /** Passo nominale della griglia in px. Ridotto se il contenitore e' stretto. Default 72. */
  cellPitch?: number;
  /** Velocita' di deriva in rad/s. Default [0.22, 0.62]. */
  speedRange?: [number, number];
  /**
   * Raggio d'orbita come frazione del passo (0-0.5). Default 0.4.
   *
   * Il tetto non e' prudenza generica. Il componente taglia ogni cella contro una
   * lista di vicini calcolata UNA VOLTA sulle posizioni a riposo: se la deriva
   * porta un seme ad avere un vicino vero fuori da quella lista, la sua cella
   * esce piu' grande del dovuto e si sovrappone alle altre. Misurando la somma
   * delle aree di tutte le celle - che con un ritaglio esatto vale l'area del box
   * - l'errore resta 0.000% fino a 0.55 e diventa 0.065% a 0.65. Il tetto sta
   * sotto quella soglia con margine.
   */
  orbitScale?: number;
  /**
   * Fattore sulla scala della cella della foto. La cella si adatta al rettangolo
   * misurato di focusRef con una scala uniforme: 1 la fa combaciare con quel
   * rettangolo, valori maggiori la fanno sporgere. Default 1.
   */
  focusPadding?: number;
  /** Probabilita' che una cella riceva il riempimento d'accento. Default 0.06. */
  accentChance?: number;
  /** Congela la deriva. Un fotogramma viene comunque disegnato. Default false. */
  paused?: boolean;
  /**
   * Se true, `prefers-reduced-motion: reduce` congela la deriva come farebbe
   * paused. Default true.
   *
   * Va saputo che questa impostazione, su Windows, e' la stessa che spegne le
   * animazioni di sistema (Accessibilita' > Effetti visivi > Effetti animazione):
   * e' attiva su molte macchine per ragioni che con le vertigini non hanno nulla
   * a che fare, e chi sviluppa il sito vede allora uno sfondo perennemente fermo
   * senza capire perche'. Il default resta true perche' la preferenza e'
   * dell'utente, ma ora si puo' dire di no.
   */
  respectReducedMotion?: boolean;
  className?: string;
}

type Pt = [number, number];

interface Seed {
  /** Posizione a riposo: fissa, e' la chiave di ogni decisione strutturale. */
  bx: number;
  by: number;
  /** Posizione corrente. */
  x: number;
  y: number;
  rx: number;
  ry: number;
  wx: number;
  wy: number;
  px: number;
  py: number;
  /** Semi che possono modellare questa cella. Calcolati una volta: le posizioni
   *  a riposo non cambiano, quindi non cambia nemmeno l'insieme dei vicini. */
  neigh: number[];
  /** null per i semi che modellano senza essere disegnati. */
  el: SVGPolygonElement | null;
  /** true per l'ospite e per l'anello: non derivano. */
  fixed: boolean;
}

/** Taglia `poly` col semipiano dei punti piu' vicini a (sx,sy) che a (ox,oy). */
function clipHalfPlane(poly: Pt[], sx: number, sy: number, ox: number, oy: number): Pt[] {
  const mx = (sx + ox) / 2;
  const my = (sy + oy) / 2;
  const dx = ox - sx;
  const dy = oy - sy;
  const out: Pt[] = [];
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    const dp = (p[0] - mx) * dx + (p[1] - my) * dy;
    const dq = (q[0] - mx) * dx + (q[1] - my) * dy;
    if (dp <= 0) out.push(p);
    // I due lati del semipiano estratti in variabili e non confrontati inline:
    // `dp < 0 !== dq < 0` si legge male e fa scattare no-mixed-operators, e le
    // parentesi che lo zittirebbero vengono togliute da prettier come ridondanti.
    const pInside = dp < 0;
    const qInside = dq < 0;
    if (pInside !== qInside) {
      const t = dp / (dp - dq);
      out.push([p[0] + t * (q[0] - p[0]), p[1] + t * (q[1] - p[1])]);
    }
  }
  return out;
}

function pointsAttr(poly: Pt[]): string {
  let s = '';
  for (let i = 0; i < poly.length; i++) {
    s += poly[i][0].toFixed(1) + ',' + poly[i][1].toFixed(1) + ' ';
  }
  return s;
}

/**
 * Rampa di riempimento sulla profondita' normalizzata t (0 = alto, 1 = linea di
 * taglio). Parte da --header-color (#2b3d38, hsl 163 17% 20%) del tema casual e
 * va verso --bs-primary, ma SENZA arrivarci.
 *
 * Il vincolo e' il testo bianco dell'header (.text-light, #f8f9fa), che scorre
 * sopra le celle su tutta la fascia centrale. Il contrasto va misurato, non
 * stimato: arrivando fino a --bs-primary pieno (hsl 166 44% 53%) il fondo rampa
 * dava 2.19:1, cioe' testo praticamente illeggibile. Il tetto di luminosita' per
 * stare a 4.5:1 (WCAG AA, testo normale) e' ~34% a questa saturazione, e la
 * rampa si ferma li': il fondo misura 4.67:1 e nessun punto scende sotto.
 *
 * L'escursione di luminosita' e' quindi meta' di prima. La lettura di
 * profondita' la porta ora soprattutto la SATURAZIONE, che sale come prima da 17
 * a 44: le celle in basso restano visibilmente piu' "vive" senza schiarirsi.
 */
function rampFill(t: number): string {
  const h = 163 + 3 * t;
  const s = 17 + 27 * t;
  const l = 20 + 14 * t;
  return `hsl(${h.toFixed(1)} ${s.toFixed(1)}% ${l.toFixed(1)}%)`;
}

/**
 * Cella-accento. Era hsl(166 58% 68%), il verde acceso: 1.53:1 col testo bianco,
 * il punto peggiore di tutto l'header.
 *
 * Sopra il tetto di luminosita' spiegato in rampFill non c'e' spazio per un
 * accento PIU' CHIARO che resti leggibile - a pari luminosita' della rampa un
 * accento piu' saturo dista appena 17 in RGB e non si distingue. L'accento va
 * quindi cercato nella TINTA: virando al ciano si stacca dal verde della rampa
 * restando cupo. 6.18:1, e distanza 28 dal fondo rampa.
 */
const ACCENT_FILL = 'hsl(186 55% 28%)';
/** Riempimento della macchia ospite: piu' cupo della rampa, perche' l'immagine
 *  del profilo gli sta sopra e deve staccare. */
const FOCUS_FILL = 'hsl(163 24% 14%)';
const NS = 'http://www.w3.org/2000/svg';
/** Serve solo per il fallback di href su <image>. Vedi dove viene usato. */
const XLINK_NS = 'http://www.w3.org/1999/xlink';

/** Fuori dal componente di proposito: un letterale nella firma sarebbe un array
 *  nuovo a ogni render, quindi una dipendenza nuova per build(). Vedi wMin/wMax. */
const DEFAULT_SPEED_RANGE: [number, number] = [0.25, 0.7];

export default function VoronoiHeader({
  focusRef,
  cellPitch = 72,
  speedRange = DEFAULT_SPEED_RANGE,
  orbitScale = 0.4,
  focusPadding = 1,
  accentChance = 0.06,
  paused = false,
  respectReducedMotion = true,
  className,
}: VoronoiHeaderProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const seedsRef = useRef<Seed[]>([]);
  const dimsRef = useRef({ W: 0, H: 0, Hext: 0 });
  const rafRef = useRef<number | null>(null);
  // Un id per istanza. useId contiene caratteri che in un selettore CSS
  // andrebbero sfuggiti, e qui l'id finisce in url(#...): si ripulisce e basta.
  const clipId = `voronoi-focus-clip-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const pausedRef = useRef(paused);
  /** Deriva sospesa perche' il sistema chiede meno movimento E il chiamante ha
   *  scelto di rispettarlo. Un ref e non uno stato: cambia il comportamento del
   *  loop, non l'albero renderizzato. */
  const reducedRef = useRef(false);

  pausedRef.current = paused;

  // Destrutturato qui e non dentro build(): le dipendenze di build() devono
  // essere numeri, non l'array. Con l'array in lista, un chiamante che passasse
  // speedRange come letterale inline - o il default, prima che diventasse una
  // costante di modulo - darebbe a build() un'identita' nuova a ogni render:
  // l'effetto si rimonterebbe e la tassellazione verrebbe ricostruita da zero,
  // con jitter e orbite riestratti. Header rirenderizza a ogni minificazione,
  // quindi il mosaico si ririfaceva sotto gli occhi durante lo scroll.
  const [wMin, wMax] = speedRange;

  /** Aggiorna le celle che derivano. La cella ospite non e' fra queste. */
  const renderFrame = useCallback((tSec: number) => {
    const { W, Hext } = dimsRef.current;
    const seeds = seedsRef.current;
    if (W === 0 || seeds.length === 0) return;

    for (let i = 0; i < seeds.length; i++) {
      const s = seeds[i];
      if (s.fixed) continue;
      s.x = s.bx + Math.cos(tSec * s.wx + s.px) * s.rx;
      s.y = s.by + Math.sin(tSec * s.wy + s.py) * s.ry;
    }

    const bbox: Pt[] = [
      [0, 0],
      [W, 0],
      [W, Hext],
      [0, Hext],
    ];

    for (let i = 0; i < seeds.length; i++) {
      const s = seeds[i];
      if (s.el === null) continue;
      // L'ospite e' l'indice 0 quando esiste: gia' disegnato in build(), mai
      // ridisegnato. E' cosi' che la macchia non cambia dimensione.
      if (s.fixed && i === 0) continue;

      let poly = bbox;
      for (let k = 0; k < s.neigh.length; k++) {
        const o = seeds[s.neigh[k]];
        poly = clipHalfPlane(poly, s.x, s.y, o.x, o.y);
        if (poly.length === 0) break;
      }
      if (poly.length >= 3) s.el.setAttribute('points', pointsAttr(poly));
    }
  }, []);

  const build = useCallback(() => {
    const host = hostRef.current;
    const svg = svgRef.current;
    if (!host || !svg) return;

    const W = host.clientWidth;
    const Hhost = host.clientHeight;
    // Layout non pronto (tab nascosta, primo paint): il ResizeObserver richiama.
    if (W < 40 || Hhost < 40) return;

    const cell = Math.max(40, Math.min(cellPitch, W / 16));
    // La frangia vive DENTRO il box dell'header: la linea di taglio sale di
    // altrettanto. Cosi' niente sborda, .header conserva overflow: hidden e
    // nessun contenuto sotto va spostato per fare spazio.
    const fringe = Math.min(76, Math.max(26, cell * 1.15));
    const H = Hhost - fringe;
    const Hext = Hhost;
    dimsRef.current = { W, H, Hext };

    svg.setAttribute('viewBox', `0 0 ${W} ${Hext}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.textContent = '';

    const maxOrbit = cell * Math.min(0.5, Math.max(0, orbitScale));
    const bbox: Pt[] = [
      [0, 0],
      [W, 0],
      [W, Hext],
      [0, Hext],
    ];

    // --- misura del fuoco ------------------------------------------------
    // Non serve un raggio: la forma della cella e' data, serve solo come portarla
    // dal suo sistema locale a questo. Una scala UNIFORME, non una per asse: la
    // cella e la foto devono conservare le proporzioni con cui sono disegnate, e
    // il rettangolo misurato fa loro solo da contenitore. Il minimo dei due
    // rapporti e' la scala che ci sta dentro su entrambi gli assi.
    let focus: { cx: number; cy: number; scale: number } | null = null;
    const fe = focusRef?.current ?? null;
    if (fe) {
      const fr = fe.getBoundingClientRect();
      const hr = host.getBoundingClientRect();
      if (fr.width > 8 && fr.height > 8) {
        focus = {
          cx: fr.left - hr.left + fr.width / 2,
          cy: fr.top - hr.top + fr.height / 2,
          scale: Math.min(fr.width / CELL_W, fr.height / CELL_H) * focusPadding,
        };
      }
    }

    // La cella della foto in coordinate di QUESTO svg: il centro del viewBox
    // locale finisce nel centro del rettangolo misurato. E' l'unica conversione
    // fra i due sistemi, e la usano sia il poligono sia l'immagine.
    // f e non focus: focus e' un let, e TypeScript scarta il narrowing dentro un
    // callback perche' non puo' escludere una riassegnazione nel frattempo.
    const f = focus;
    const focusCell: Pt[] | null = f
      ? CELL_LOCAL.map(
          ([x, y]): Pt => [f.cx + (x - CELL_W / 2) * f.scale, f.cy + (y - CELL_H / 2) * f.scale]
        )
      : null;

    const seeds: Seed[] = [];

    const makeEl = (fill: string, focusCell: boolean): SVGPolygonElement => {
      const el = document.createElementNS(NS, 'polygon');
      el.setAttribute('fill', fill);
      el.setAttribute('class', focusCell ? 'voronoi-header__focus' : 'voronoi-header__cell');
      svg.appendChild(el);
      return el;
    };

    // --- ospite + semi specchiati sui lati della cella -------------------
    let hostPoly: Pt[] | null = null;
    if (focus && focusCell) {
      const { cx, cy } = focus;

      // L'ospite occupa l'indice 0: renderFrame lo riconosce e lo salta.
      seeds.push({
        bx: cx,
        by: cy,
        x: cx,
        y: cy,
        rx: 0,
        ry: 0,
        wx: 0,
        wy: 0,
        px: 0,
        py: 0,
        neigh: [],
        el: null,
        fixed: true,
      });

      // Un seme per LATO, nel punto simmetrico dell'ospite rispetto a quel lato.
      // L'asse fra due semi e' la mediana del segmento che li unisce: riflettendo
      // sul lato, quell'asse E' il lato. Fatto per ogni lato di un poligono
      // convesso che contiene l'ospite, la cella dell'ospite - intersezione dei
      // semipiani - coincide col poligono.
      //
      // Sostituisce un anello di semi a raggio 2R con angoli casuali, che dava una
      // macchia irregolare ma di forma non governabile: la cella della foto veniva
      // allora disegnata sopra, e fra quella forma e la cella vera del seme
      // restava una fascia che nessuno riempiva.
      const mirrorStart = seeds.length;
      for (let i = 0; i < focusCell.length; i++) {
        const a = focusCell[i];
        const b = focusCell[(i + 1) % focusCell.length];
        const ex = b[0] - a[0];
        const ey = b[1] - a[1];
        const len = Math.hypot(ex, ey);
        if (len < 1e-6) continue;
        // Normale unitaria al lato, e distanza con segno dell'ospite da esso.
        const nx = -ey / len;
        const ny = ex / len;
        const d = (cx - a[0]) * nx + (cy - a[1]) * ny;
        const bx = cx - 2 * d * nx;
        const by = cy - 2 * d * ny;
        seeds.push({
          bx,
          by,
          x: bx,
          y: by,
          rx: 0,
          ry: 0,
          wx: 0,
          wy: 0,
          px: 0,
          py: 0,
          neigh: [],
          el: null,
          fixed: true,
        });
      }

      // Ricavata come per ogni altra cella, non assegnata: e' la prova che la
      // costruzione a specchio funziona. Se i semi sono giusti, questo taglio
      // riproduce focusCell da solo.
      hostPoly = bbox;
      for (let i = mirrorStart; i < seeds.length; i++) {
        hostPoly = clipHalfPlane(hostPoly, cx, cy, seeds[i].bx, seeds[i].by);
      }
    }

    /**
     * Proiezione massima della cella ospite lungo la direzione (ux,uy), misurata
     * dal centro. Un seme a distanza d genera un lato a d/2: se
     * d/2 >= maxProj, il semipiano contiene tutta la cella e non la tocca.
     */
    const maxProjection = (ux: number, uy: number): number => {
      if (!hostPoly || !focus) return 0;
      let m = 0;
      for (let i = 0; i < hostPoly.length; i++) {
        const p = (hostPoly[i][0] - focus.cx) * ux + (hostPoly[i][1] - focus.cy) * uy;
        if (p > m) m = p;
      }
      return m;
    };

    /** Un seme della griglia e' ammesso solo se non puo' mordere la macchia. */
    const clearsFocus = (gx: number, gy: number): boolean => {
      if (!focus) return true;
      const dx = gx - focus.cx;
      const dy = gy - focus.cy;
      const d = Math.hypot(dx, dy);
      if (d < 1) return false;
      // maxOrbit: il seme deriva, e il vincolo deve valere nella posizione
      // piu' sfavorevole che potra' assumere.
      return d >= 2 * maxProjection(dx / d, dy / d) + maxOrbit + 2;
    };

    // --- griglia a densita' variabile per distribuzione di dimensioni ---------
    // Distribuzione verticale: piccole in alto, grandi attorno alla foto, poi di
    // nuovo piccole, e micro-macchie sull'ultimo strato. Transizione graduale.
    //
    // Probabilita' di TENERE un seme, in funzione della quota. Vicino alla foto
    // se ne tengono meno (celle grandi), lontano tutti (celle piccole).
    const keepProbability = (y: number): number => {
      if (!focus) return 1;
      const dy = Math.abs(y - focus.cy);
      // Sigma legata all'altezza visibile: la fascia di celle grandi resta
      // proporzionata sia con header a 100vh sia minificato a 300px.
      const sigma = Math.max(40, H * 0.14);
      const gaussian = Math.exp(-Math.pow(dy / sigma, 2));
      // centro foto -> 0.55 (celle grandi), lontano -> 1 (celle piccole)
      return 1 - gaussian * 0.45;
    };

    /** Un seme, con orbita proporzionata al proprio passo locale. */
    const pushSeed = (bx: number, by: number, orbit: number) => {
      seeds.push({
        bx,
        by,
        x: bx,
        y: by,
        rx: orbit * (0.45 + Math.random() * 0.55),
        ry: orbit * (0.45 + Math.random() * 0.55),
        wx: wMin + Math.random() * (wMax - wMin),
        wy: wMin + Math.random() * (wMax - wMin),
        px: Math.random() * Math.PI * 2,
        py: Math.random() * Math.PI * 2,
        neigh: [],
        el: null,
        fixed: false,
      });
    };

    // Quota da cui parte lo strato di micro-macchie: l'ultima fascia visibile,
    // quella che il taglio inferiore frastaglia. La griglia normale si ferma
    // qui, cosi' i due passi non si sovrappongono.
    const microTop = H - cell * 0.9;

    const cols = Math.ceil(W / cell) + 1;
    const rows = Math.ceil(Hext / cell) + 1;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const bx = (c + 0.5 + (Math.random() - 0.5) * 0.5) * cell;
        const by = (r + 0.5 + (Math.random() - 0.5) * 0.5) * cell;

        if (by >= microTop) continue;
        if (Math.random() > keepProbability(by)) continue;
        if (!clearsFocus(bx, by)) continue;

        pushSeed(bx, by, maxOrbit);
      }
    }

    // --- ultimo strato: micro-macchie -------------------------------------
    // Passo dimezzato sotto microTop, giu' fino al fondo del box: il bordo
    // frastagliato risulta cosi' fatto di schegge, non di macchie intere.
    // L'orbita scala col passo, altrimenti la deriva - tarata sul passo grande -
    // porterebbe questi semi l'uno sull'altro.
    const microCell = cell * 0.3;
    const microCols = Math.ceil(W / microCell) + 1;
    const microRows = Math.ceil((Hext - microTop) / microCell) + 1;
    const microOrbit = maxOrbit * 0.3;
    for (let r = 0; r < microRows; r++) {
      for (let c = 0; c < microCols; c++) {
        const bx = (c + 0.5 + (Math.random() - 0.5) * 0.5) * microCell;
        const by = microTop + (r + 0.5 + (Math.random() - 0.5) * 0.5) * microCell;

        if (by < microTop) continue;
        if (!clearsFocus(bx, by)) continue;

        pushSeed(bx, by, microOrbit);
      }
    }

    // --- chi viene disegnato: deciso qui, una volta ------------------------
    // Sotto la linea di taglio il seme resta nel diagramma e modella i vicini,
    // ma non ha un poligono: il bordo inferiore respira senza che niente
    // compaia o scompaia.
    const specialCount = focus && focusCell ? 1 + focusCell.length : 0;
    for (let i = 0; i < seeds.length; i++) {
      const s = seeds[i];

      // L'ospite si disegna sempre, senza test di profondita': e' il piano
      // d'appoggio dell'immagine del profilo. Se un layout lo portasse sotto la
      // linea di taglio, il test lo scarterebbe e l'immagine resterebbe
      // appoggiata sul nulla - il caso peggiore possibile per questo componente.
      if (focus && i === 0) {
        s.el = makeEl(FOCUS_FILL, true);
        continue;
      }

      // Profondita' su cui si decide il taglio, che NON e' sempre la quota del
      // seme. Per un seme della griglia lo e': la cella gli sta attorno, e un
      // seme sotto la linea porta sotto anche la propria cella - e' cosi' che
      // nasce il bordo frastagliato. Per un seme specchiato no: la sua cella sta
      // dalla parte opposta del lato rispetto a lui, verso l'ospite. Misurarla
      // sul seme scarta celle che coprono ancora fascia visibile e apre un cuneo
      // trasparente a fianco della foto.
      //
      // Il punto medio fra ospite e seme sta sul lato, cioe' sul bordo interno
      // della cella. Per un seme sotto il centro e' il punto meno profondo che la
      // cella raggiunge; per uno sopra e' il piu' profondo, e usarlo da solo
      // scarterebbe celle che il criterio sulla quota teneva. Il minimo dei due vale in entrambi i versi, e per costruzione
      // non e' mai maggiore di by: il criterio nuovo puo' solo aggiungere celle,
      // mai togliere quelle che c'erano.
      const depth = focus && i < specialCount ? Math.min(s.by, (focus.cy + s.by) / 2) : s.by;
      if (depth >= H) continue;

      // La rampa segue depth e non by per la stessa ragione: una cella
      // specchiata colorata sulla quota del proprio seme prenderebbe il fondo
      // della rampa accanto alla cella piu' cupa di tutte.
      const t = Math.max(0, Math.min(1, depth / H + (Math.random() - 0.5) * 0.2));
      s.el = makeEl(Math.random() < accentChance ? ACCENT_FILL : rampFill(t), false);
    }

    // --- la foto, dentro la sua cella ------------------------------------
    // Tre elementi sullo STESSO poligono, in quest'ordine, e l'ordine e' il
    // punto:
    //
    //   riempimento -> foto ritagliata -> contorno
    //
    // Il riempimento serve perche' il PNG e' uno scontornato: attorno alla
    // persona e' trasparente, e quello che si vede li' e' il colore della cella.
    // La foto va sopra al riempimento e sopra le celle vicine, altrimenti le
    // cuciture dei vicini - che sono centrate sul confine e larghe 2px - le
    // entrerebbero dentro. Il contorno va sopra la foto, altrimenti la foto ne
    // coprirebbe la meta' interna e la fuga attorno alla cella risulterebbe
    // meta' larga di quelle fra le altre celle.
    if (focus && focusCell && seeds[0].el) {
      const pts = pointsAttr(focusCell);

      seeds[0].el.setAttribute('points', pts);

      const clip = document.createElementNS(NS, 'clipPath');
      clip.setAttribute('id', clipId);
      clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
      const clipShape = document.createElementNS(NS, 'polygon');
      clipShape.setAttribute('points', pts);
      clip.appendChild(clipShape);
      const defs = document.createElementNS(NS, 'defs');
      defs.appendChild(clip);
      svg.appendChild(defs);

      // Il riquadro dell'immagine e' la stessa conversione applicata all'intero
      // viewBox locale, quindi la foto sta nella cella come sta nel proprio
      // viewBox. "slice" la fa coprire il riquadro ritagliando l'eccesso: senza,
      // il PNG (444x550, piu' stretto del viewBox) lascerebbe bande vuote.
      const img = document.createElementNS(NS, 'image');
      img.setAttribute('x', String(focus.cx - (CELL_W / 2) * focus.scale));
      img.setAttribute('y', String(focus.cy - (CELL_H / 2) * focus.scale));
      img.setAttribute('width', String(CELL_W * focus.scale));
      img.setAttribute('height', String(CELL_H * focus.scale));
      img.setAttribute('class', 'voronoi-header__photo');
      img.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      img.setAttribute('clip-path', `url(#${clipId})`);
      // href e' la forma SVG2 e la capiscono tutti i browser recenti, ma la
      // browserslist del progetto include ios_saf 11, che conosce solo
      // xlink:href: senza il fallback li' la foto semplicemente non comparirebbe,
      // in silenzio. Chi supporta href ignora xlink:href, quindi metterli
      // entrambi non ha controindicazioni.
      img.setAttribute('href', casualVoronoiPhoto);
      img.setAttributeNS(XLINK_NS, 'xlink:href', casualVoronoiPhoto);
      svg.appendChild(img);

      const outline = document.createElementNS(NS, 'polygon');
      outline.setAttribute('points', pts);
      outline.setAttribute('class', 'voronoi-header__focus-outline');
      svg.appendChild(outline);
    }

    // --- vicini: insieme fisso, calcolato una volta -----------------------
    // Ospite e anello entrano nella lista di TUTTI: sono pochi, e attorno al
    // vuoto sono i soli vicini possibili, a distanza ben oltre il raggio di
    // ricerca sulla griglia.
    const reach = cell * 3;
    for (let i = 0; i < seeds.length; i++) {
      const s = seeds[i];
      if (s.el === null) continue;
      const list: number[] = [];
      for (let j = 0; j < specialCount; j++) {
        if (j !== i) list.push(j);
      }
      for (let j = specialCount; j < seeds.length; j++) {
        if (j === i) continue;
        const o = seeds[j];
        if (Math.abs(o.bx - s.bx) > reach || Math.abs(o.by - s.by) > reach) continue;
        list.push(j);
      }
      s.neigh = list;
    }

    seedsRef.current = seeds;
    // Primo fotogramma sincrono: una tab nascosta non emette mai un rAF, e
    // l'header resterebbe vuoto fino al primo focus.
    renderFrame(performance.now() / 1000);
  }, [
    cellPitch,
    wMin,
    wMax,
    orbitScale,
    focusPadding,
    accentChance,
    clipId,
    focusRef,
    renderFrame,
  ]);

  // prefers-reduced-motion vale come un paused imposto dal sistema. Il primo
  // fotogramma viene comunque disegnato da build(), quindi la tassellazione si
  // vede: e' solo ferma. Il progetto rispetta gia' questa preferenza altrove
  // (CVChat.scss), e qui il movimento e' l'intero contenuto dello sfondo.
  useEffect(() => {
    if (!respectReducedMotion) {
      reducedRef.current = false;
      return;
    }
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => {
      reducedRef.current = mq.matches;
      // Uno sfondo fermo e' indistinguibile da uno sfondo rotto, e la causa sta
      // in un'impostazione di sistema che non si pensa di andare a guardare. In
      // sviluppo lo si dice, una volta, invece di lasciarlo indovinare.
      if (mq.matches && process.env.NODE_ENV === 'development') {
        console.info(
          '[VoronoiHeader] deriva congelata: il sistema chiede ' +
            'prefers-reduced-motion. Su Windows: Impostazioni > Accessibilita > ' +
            'Effetti visivi > Effetti animazione. Per ignorare la preferenza, ' +
            'passare respectReducedMotion={false}.'
        );
      }
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [respectReducedMotion]);

  useEffect(() => {
    build();

    const host = hostRef.current;
    const focusEl = focusRef?.current ?? null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // L'header transisce l'altezza (0.3s) e l'immagine del profilo larghezza e
    // altezza (0.5s): durante la minificazione arrivano decine di notifiche.
    // Il debounce le fonde e ricostruisce una volta, a transizione finita.
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(build, 180);
    });
    if (host) ro.observe(host);
    if (focusEl) ro.observe(focusEl);

    // Il loop resta iscritto anche da fermo: la preferenza di sistema puo'
    // cambiare mentre la pagina e' aperta, e riagganciare un rAF da un listener
    // costerebbe piu' codice del fotogramma che si salta.
    const loop = (now: number) => {
      if (!pausedRef.current && !reducedRef.current) renderFrame(now / 1000);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      ro.disconnect();
      clearTimeout(timer);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [build, renderFrame, focusRef]);

  return (
    <div ref={hostRef} className={`voronoi-header${className ? ' ' + className : ''}`}>
      <svg ref={svgRef} className="voronoi-header__svg" aria-hidden="true" focusable="false" />
    </div>
  );
}
